import type { SeriesTask, UUID } from "@/lib/types";

export type TaskFilter = "all" | "open" | "done";

/** All / Offen / Erledigt counts for the Aufgaben filter pills. Pure so the
 * count logic can be unit-tested without rendering. */
export function seriesTaskCounts(tasks: SeriesTask[]): {
  all: number;
  open: number;
  done: number;
} {
  let done = 0;
  for (const task of tasks) if (task.done) done += 1;
  return { all: tasks.length, open: tasks.length - done, done };
}

/** Apply the active filter to the task list. Pure — shared by the component
 * and its tests. */
export function filterSeriesTasks(tasks: SeriesTask[], filter: TaskFilter): SeriesTask[] {
  if (filter === "open") return tasks.filter((task) => !task.done);
  if (filter === "done") return tasks.filter((task) => task.done);
  return tasks;
}

export interface TaskGroups {
  /** Assigned to the signed-in user. */
  mine: SeriesTask[];
  /** Everyone else's, plus anything unassigned. */
  others: SeriesTask[];
}

/**
 * Split the series' to-dos into "mine" and "the rest", preserving order within each.
 *
 * 🔴 A SPLIT, NOT A FILTER. The room sees every to-do — accountability in a Besprechungsreihe
 * is public, and hiding other people's work would quietly turn a shared roster into a private
 * one. Mine simply come FIRST, because that is what I am scanning the tab for. Someone who
 * wants only their own can say so explicitly with the "Nur meine" toggle.
 *
 * `meId` null (the /auth/me query has not landed yet) ⇒ nothing is "mine", so the list renders
 * ungrouped rather than flickering a wrong grouping into place and then correcting itself.
 * This falls out of the comparison below — an id is a string, so nothing equals null — and
 * needs no guard of its own. An early return here was DEAD CODE: no mutant could kill it,
 * which is precisely how you know a line is doing nothing.
 *
 * 🔴 Compares the INTERNAL users.id, not the Keycloak sub: every person reference in Flow — a
 * person cell, a memo assignee — is the internal id, and the OIDC token does not carry it.
 */
export function partitionByAssignee(tasks: SeriesTask[], meId: UUID | null): TaskGroups {
  const mine: SeriesTask[] = [];
  const others: SeriesTask[] = [];
  for (const task of tasks) {
    if (task.assignee?.id === meId) mine.push(task);
    else others.push(task);
  }
  return { mine, others };
}
